import i18n from "../i18n";
import { documentsDic } from "./dataDictionary";

export const dt = (key, opts) => i18n.t(`documents:${key}`, opts);

const DOC_CODE_KEYS = {
  0: "boletaHonorarios",
  33: "facturaElectronica",
  34: "facturaExenta",
  61: "notaCredito",
  56: "notaDebito",
  46: "facturaCompraElectronica",
  110: "facturaExportacion",
  111: "notaDebitoExportacion",
  112: "notaCreditoExportacion",
  1000: "obligacion",
};

const SUMMARY_ALIASES = {
  "Factura de Venta": "facturaElectronica",
  "Factura de venta": "facturaElectronica",
  "FACTURA ELECTRONICA": "facturaElectronica",
  "Factura Electrónica Afecta": "facturaElectronica",
  "Boleta de Honorarios Electrónica": "boletaHonorarios",
};

const buildSummaryKeyMap = () => {
  const map = { ...SUMMARY_ALIASES };
  Object.entries(documentsDic).forEach(([code, val]) => {
    const docKey = DOC_CODE_KEYS[parseInt(code, 10)];
    if (!docKey) return;
    if (val.fullName) map[val.fullName] = docKey;
    if (val.name && typeof val.name === "string") map[val.name] = docKey;
  });
  return map;
};

const SUMMARY_KEY_MAP = buildSummaryKeyMap();

export function translateDocumentFullName(code) {
  const parsed = parseInt(code, 10);
  const key = DOC_CODE_KEYS[parsed];
  if (key) {
    const translated = dt(`documentCodes.${key}`);
    if (translated && translated !== `documentCodes.${key}`) return translated;
  }
  return documentsDic[parsed]?.fullName || documentsDic[parsed]?.name || String(code);
}

export function translateDocumentSummary(summary) {
  if (!summary || typeof summary !== "string") return summary || "";
  const trimmed = summary.trim();
  const docKey = SUMMARY_KEY_MAP[trimmed];
  if (docKey) {
    const translated = dt(`documentCodes.${docKey}`);
    if (translated && translated !== `documentCodes.${docKey}`) return translated;
  }
  return trimmed;
}

export function getDocumentTypeDisplayLabel(data) {
  if (!data) return "";
  if (data.documentType === "OBLIGATION") {
    return data.meta?.typeLabel || dt("documentCodes.obligacion");
  }
  const code = parseInt(data.meta?.documentCode, 10);
  if (!Number.isNaN(code) && DOC_CODE_KEYS[code]) {
    return translateDocumentFullName(code);
  }
  return (
    translateDocumentSummary(data.meta?.summary) ||
    translateDocumentSummary(data.meta?.typeLabel) ||
    dt("common.document")
  );
}

export function getDocumentFilterTabs(pathname) {
  if (pathname === "/cuotas_de_creditos") {
    return [
      { label: "CLP", value: "CLP" },
      { label: "USD", value: "USD" },
      { label: "UF", value: "UF" },
    ];
  }
  if (pathname === "/boletas") {
    return [
      { labelKey: "tabs.fees", value: "FEES" },
      { labelKey: "tabs.terceros", value: "TERCEROS" },
    ];
  }
  if (pathname === "/obligaciones") {
    return [
      { labelKey: "tabs.obligations", value: "OBLIGATION" },
      { labelKey: "tabs.invoices", value: "INVOICES" },
      { labelKey: "tabs.estimations", value: "ESTIMACION" },
    ];
  }
  if (pathname === "/declaraciones_de_ingresos") {
    return [{ labelKey: "tabs.din", value: "DIN" }];
  }
  return [
    { labelKey: "tabs.all", value: "ALL" },
    { labelKey: "tabs.salesBook", value: "GIVEN" },
    { labelKey: "tabs.purchaseBook", value: "RECEIVED" },
  ];
}

export function getDocumentStatusOptions() {
  return [
    { text: dt("filters.status.paid"), value: "isPaid" },
    { text: dt("filters.status.unpaid"), value: "notPaid" },
    { text: dt("filters.status.accepted"), value: "accepted" },
    { text: dt("filters.status.pendingAccept"), value: "notAccepted" },
    { text: dt("filters.status.protested"), value: "isProtested" },
    { text: dt("filters.status.voided"), value: "notValid" },
    { text: dt("filters.status.factored"), value: "isFactoring" },
    { text: dt("filters.status.reconciled"), value: "isReconciled" },
    { text: dt("filters.status.paidManually"), value: "isPaidManually" },
  ];
}

export function getDocumentTypeFilterOptions() {
  return [
    { key: "TAX_DOCUMENT", text: dt("filters.documentTypes.tax"), value: "TAX_DOCUMENT" },
    { key: "OBLIGATION", text: dt("filters.documentTypes.obligation"), value: "OBLIGATION" },
  ];
}

export function isPeriodClosedError(message) {
  if (!message || typeof message !== "string") return false;
  const lower = message.toLowerCase();
  return (
    (lower.includes("período") || lower.includes("periodo")) &&
    lower.includes("cerrado")
  );
}

const OBLIGATION_TYPE_ALIASES = {
  pago_en_efectivo: "pagoenefectivo",
  diferencia_tc: "diferenciadetipodecambio",
};

export function translateObligationType(typeKey) {
  if (!typeKey) return dt("reconcile.unidentifiedObligation");
  const normalized = String(typeKey).replace(/_/g, "").toLowerCase();
  const alias = OBLIGATION_TYPE_ALIASES[typeKey] || OBLIGATION_TYPE_ALIASES[normalized];
  const keys = [
    alias,
    normalized,
    String(typeKey).toLowerCase(),
    String(typeKey).replace(/_/g, "_").toLowerCase(),
  ].filter(Boolean);
  for (const key of keys) {
    const translated = dt(`reconcile.obligationTypes.${key}`);
    if (translated && translated !== `reconcile.obligationTypes.${key}`) return translated;
  }
  return (
    String(typeKey)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .trim() || dt("reconcile.unidentifiedObligation")
  );
}

export function getDocumentDetailTabs(commentCount = 0) {
  const cap = commentCount > 99 ? "99+" : commentCount;
  return [
    { key: "detalles", labelKey: "detail.tabs.details" },
    { key: "matches", labelKey: "detail.tabs.matches" },
    { key: "nominas", labelKey: "detail.tabs.payrolls" },
    { key: "parametrizacion", labelKey: "detail.tabs.settings" },
    {
      key: "comentarios",
      labelKey: "detail.tabs.comments",
      count: commentCount > 0 ? cap : undefined,
    },
  ];
}

export function getReconcileSidebarTabs(counts = {}) {
  const { matches = 0, entries = 0, comments = 0, files = 0 } = counts;
  const cap = (n) => (n > 99 ? "99+" : n);
  return [
    { value: "match", labelKey: "reconcile.tabs.matches", count: cap(matches) },
    { value: "contabilizar", labelKey: "reconcile.tabs.entries", count: cap(entries) },
    { value: "notes", labelKey: "reconcile.tabs.comments", count: cap(comments) },
    { value: "files", labelKey: "reconcile.tabs.files", count: cap(files) },
  ];
}
